import { h } from '../lib/dom.js';
import { getState, setSilent, fmtNum, fmtReporting, toReporting, totalUSDFor, CONFIG, explorerTxUrl, swapDeliverAvailable } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  setFromAccount, setMoveField, setSendBalance, onMoveContinue, onMoveBack, onMoveConfirm, onMoveDone,
  openAddRecipient, openEditRecipient, validateMoveAmountBlur, go, openOpenAccount, openInNewWindow,
  setMoveMemo, MEMO_MAX_LEN, requestMoveLimitOverride, requestNewRecipientOverride, NATIVE_CCY, setDeliverCcy
} from '../actions.js';
import { renderActivityRail } from './activityRail.js';

// Currency-conversion "deliver as" (feature-swap.md) — pure, DOM-free
// decisions extracted per CLAUDE.md's Testing section ("no DOM environment
// installed... extract the pure logic... so it can be unit-tested without a
// DOM at all"), exported so Transfer's screen (Phase 6) shares them instead
// of reimplementing. `swapDeliverAvailable` already centralizes "is this
// chain even eligible" (store.js); these two centralize the two remaining
// swap-specific UI decisions.

// Options for the "deliver as" select — the four other stablecoins, or []
// when unavailable: a reporting-currency amount entry (Scope: "not combined
// with the fiat-reporting-currency amount entry in v1"), testnet (Phase 0:
// Velora has zero Sepolia liquidity — never offer a control that would only
// ever error), or a chain that doesn't resolve to Ethereum (e.g. USDT via
// Send, which defaults to Tron and is never auto-forced — Phase 4).
export function deliverOptionsFor(ccy, reportingCurrency, testnetEnabled) {
  if (ccy === reportingCurrency || testnetEnabled) return [];
  if (!swapDeliverAvailable(CONFIG.NET[ccy] || '')) return [];
  return Object.keys(CONFIG.CHAINS).filter((c) => c !== ccy);
}

// The swap review row's fee value — mutually exclusive by design (Phase 2's
// quoteSwapDeliver): while an approval is still needed, only the approve
// leg's own fee is knowable yet; once approved (or never needed), the
// swap's own fee is. Returns null while genuinely unresolved (caller
// decides the "Estimating…" copy), the error string verbatim, or a
// formatted "amount CCY" string.
export function swapFeeDisplay(m, feeCcy, nativeCcy) {
  if (m.reviewSwapError) return m.reviewSwapError;
  const needsApproval = m.reviewSwapNeedsApproval;
  const amount = needsApproval ? m.reviewSwapApproveFee : m.reviewSwapFee;
  const asset = needsApproval ? m.reviewSwapApproveFeeAsset : m.reviewSwapFeeAsset;
  if (amount == null) return null;
  return amount + ' ' + (asset === 'token' ? feeCcy : nativeCcy);
}

// Same Home-style balance box as Transfer's (src/screens/transfer.js) —
// clicking a row both picks that currency and fills Amount with the full
// balance, replacing the removed "Max" button (see setSendBalance).
// readOnly (renderForm's step === 'review' flag, threaded through here
// too): setSendBalance changes m.ccy/m.amount — exactly the values the
// review pane is showing — via setState, not setSilent, so it wouldn't
// silently desync the review from what's about to be signed the way the
// text inputs could, but it would still let a click change what's under
// review without ever going back through onMoveContinue. A div onclick
// has no `disabled` attribute to lean on, so the row itself drops its
// clickable class/handler here instead — same "locked until Back"
// guarantee the rest of renderForm's fields already give.
// Same visually-hidden-native-checkbox + lucide square/square-check icon
// pattern as landing.js's onboarding checkboxes (customCheckbox there),
// but with light-card-appropriate colors — landing.js's version hardcodes
// white-when-unchecked for its dark hero background, which would be
// invisible against this screen's light review card. Duplicated locally
// rather than shared/exported since it's a 4-line pattern, not worth a
// cross-cutting refactor of an already-shipped, tested screen.
function customCheckbox(checked, onchange) {
  return h('span', { class: 'custom-checkbox-wrap' }, [
    h('input', { type: 'checkbox', class: 'custom-checkbox-input', checked, onchange }),
    h('span', { class: 'custom-checkbox-icon', 'aria-hidden': 'true', html: iconMarkup(checked ? 'squareCheck' : 'square', { size: 20, color: checked ? '#4D4DFF' : '#757993' }) })
  ]);
}

function balanceBox(w, readOnly) {
  const rows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', {
      class: 'balance-row' + (readOnly ? '' : ' balance-row-clickable'),
      onclick: readOnly ? undefined : () => setSendBalance(ccy)
    }, [
      h('span', {}, `${ccy}  ${fmtNum(amt)}`),
      h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt)))
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      h('span', { class: 'name' }, w.name),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    h('div', { class: 'owner' }, w.owner || '—'),
    h('div', { class: 'balances' }, rows)
  ]);
}

// readOnly is true exactly when the desktop split-pane (renderSend, below)
// is showing this form next to an active review pane (step === 'review').
// On mobile the form is never shown during 'review' at all — full replace,
// same as before this Desktop pass — so readOnly there is moot. On
// desktop, without this, the form stays live and editable while the
// review pane sits next to it: amount/memo use setSilent (no re-render),
// so a user could edit the amount *after* continuing to review, see the
// review pane still showing the old value (setSilent doesn't trigger one),
// and confirm — signing a different amount than what the confirmation
// screen ever displayed. That's exactly the "amount... visible before
// signing" guarantee CLAUDE.md requires, so every field that can change
// what actually gets signed is disabled here, not just visually dimmed —
// a disabled input can't fire oninput at all, keyboard tabbing skips it
// too. Going Back (onMoveBack, unchanged) is still how to make changes.
function renderForm(s, readOnly) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  const fromCcyOptions = Object.keys(curAccount.balances);
  if (!fromCcyOptions.includes(s.reportingCurrency)) fromCcyOptions.push(s.reportingCurrency);
  const availableAmt = m.ccy === s.reportingCurrency ? totalUSDFor(curAccount) : (curAccount.balances[m.ccy] || 0);
  const availableFmt = m.ccy === s.reportingCurrency ? fmtReporting(availableAmt) : fmtNum(availableAmt) + ' ' + m.ccy;
  const sendNetwork = CONFIG.NET[m.ccy] || 'Reporting currency · no network';
  const deliverOptions = deliverOptionsFor(m.ccy, s.reportingCurrency, s.testnetEnabled);

  const toOptions = [];
  s.wallets.filter((w) => w.id !== m.fromAccountId).forEach((w) => toOptions.push({ value: 'account:' + w.id, label: w.name + ' (internal)' }));
  s.savedRecipients.forEach((r) => toOptions.push({ value: 'recipient:' + r.id, label: r.name }));
  toOptions.push({ value: '__new__', label: t('send.newRecipient') });

  const fromSelect = h('select', { disabled: readOnly, onchange: (e) => setFromAccount(e.target.value) },
    s.wallets.map((w) => h('option', { value: w.id, selected: w.id === m.fromAccountId || undefined }, w.name)));

  const toSelect = h('select', {
    disabled: readOnly,
    onchange: (e) => {
      const v = e.target.value;
      if (v === '__new__') openAddRecipient();
      else setMoveField('toKey', v);
    }
  }, [
    h('option', { value: '', selected: m.toKey === '' || undefined }, t('send.selectDestination')),
    ...toOptions.map((o) => h('option', { value: o.value, selected: o.value === m.toKey || undefined }, o.label))
  ]);

  const ccySelect = h('select', { class: 'ccy-select', disabled: readOnly, onchange: (e) => setMoveField('ccy', e.target.value) },
    fromCcyOptions.map((c) => h('option', { value: c, selected: c === m.ccy || undefined }, c)));

  const [toKind, toRefId] = m.toKey ? m.toKey.split(':') : [];
  // Editing a saved recipient's address mid-review is the same desync
  // risk as the amount field — the review pane would keep showing
  // whatever address it already resolved, not the freshly edited one.
  const editRecipientBtn = toKind === 'recipient' && !readOnly
    ? h('button', { class: 'icon-btn', 'aria-label': t('send.editRecipient'), onclick: () => openEditRecipient(toRefId) }, [
        h('span', { html: iconMarkup('pencil', { size: 14 }) }), ' ' + t('common.edit')
      ])
    : null;
  const manageRecipientsBtn = !readOnly
    ? h('button', { class: 'icon-btn-plain', 'aria-label': t('send.manageRecipients'), onclick: () => go('recipients') }, h('span', { html: iconMarkup('bookOpenText', { size: 16 }) }))
    : null;

  return h('div', { class: 'stack-lg' }, [
    h('div', { class: 'flex-gap-sm items-center' }, [
      h('h1', { class: 'screen-title' }, t('send.title')),
      h('button', {
        class: 'icon-btn-plain desktop-only-icon', 'aria-label': t('nav.openInNewWindow'), onclick: () => openInNewWindow('send')
      }, h('span', { html: iconMarkup('squareArrowOutUpRight', { size: 15, color: '#757993' }) }))
    ]),
    readOnly ? h('div', { class: 'text-sm text-muted' }, t('send.formLockedWhileReviewing')) : null,
    h('div', {}, [h('div', { class: 'card-label' }, t('send.fromAccount')), fromSelect]),
    balanceBox(curAccount, readOnly),
    h('div', {}, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'card-label' }, t('send.to')),
        manageRecipientsBtn
      ]),
      h('div', { class: 'field-row' }, [toSelect, editRecipientBtn]),
      h('div', { class: 'field-error' }, m.errors.toKey || '')
    ]),
    h('div', {}, [
      h('div', { class: 'card-label' }, t('send.amount')),
      h('div', { class: 'field-row' }, [
        h('input', {
          placeholder: '0.00', value: m.amount, disabled: readOnly,
          oninput: (e) => setSilent({ move: { ...getState().move, amount: e.target.value, errors: { ...getState().move.errors, amount: null, limit: null, cooldown: null } } }),
          onblur: validateMoveAmountBlur
        }),
        ccySelect
      ]),
      h('div', { class: 'field-help' }, t('send.available', { amount: availableFmt, network: sendNetwork })),
      // feature-harden.md 6.6: per-account send limit, shown whether or
      // not it's currently exceeded — CLAUDE.md wants enforced policy
      // visible before signing, not just when it blocks something.
      curAccount.sendLimit != null
        ? h('div', { class: 'field-help' }, t('send.sendLimit', { limit: fmtReporting(curAccount.sendLimit) }))
        : null,
      h('div', { class: 'field-error' }, m.errors.amount || m.errors.limit || m.errors.cooldown || ''),
      m.errors.limit && !readOnly
        ? h('button', { class: 'btn btn-secondary btn-sm', onclick: requestMoveLimitOverride }, t('send.overrideLimit'))
        : null,
      // CCSS-KU (AUDIT.md F3): new-recipient cooldown / first-payment
      // echo-test — same override affordance as the send-limit case.
      m.errors.cooldown && !readOnly
        ? h('button', { class: 'btn btn-secondary btn-sm', onclick: requestNewRecipientOverride }, t('send.overrideNewRecipient'))
        : null
    ]),
    // Currency-conversion "deliver as" (feature-swap.md) — not shown at
    // all for a reporting-currency amount entry (Scope: out of v1 combo),
    // same as deliverOptionsFor's own reasoning. `readOnly` disables the
    // select rather than hiding it, same "locked until Back" guarantee as
    // every other field above that affects what gets signed.
    m.ccy !== s.reportingCurrency
      ? h('div', {}, [
          h('div', { class: 'card-label' }, t('send.deliverAs')),
          deliverOptions.length
            ? h('select', { disabled: readOnly, onchange: (e) => setDeliverCcy(e.target.value) }, [
                h('option', { value: '', selected: m.deliverCcy === '' || undefined }, t('send.deliverAsSame')),
                ...deliverOptions.map((c) => h('option', { value: c, selected: c === m.deliverCcy || undefined }, c))
              ])
            : h('div', { class: 'field-help' }, t('send.deliverAsUnavailable')),
          m.errors.deliverCcy ? h('div', { class: 'field-error' }, m.errors.deliverCcy) : null
        ])
      : null,
    h('div', {}, [
      h('div', { class: 'card-label' }, t('send.memo')),
      h('input', {
        placeholder: t('send.memoPlaceholder'), value: m.memo, disabled: readOnly, maxlength: String(MEMO_MAX_LEN),
        oninput: (e) => setMoveMemo(e.target.value)
      })
    ]),
    h('button', { class: 'btn btn-primary', disabled: readOnly, onclick: onMoveContinue }, t('send.continueToReview'))
  ]);
}

function renderReview(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  let reviewTo = '';
  let reviewToAddress = '';
  let recipientForReview = null;
  if (m.toKey) {
    const [kind, refId] = m.toKey.split(':');
    if (kind === 'account') {
      const w = s.wallets.find((w) => w.id === refId);
      reviewTo = w ? w.name : '';
    } else if (kind === 'recipient') {
      const r = s.savedRecipients.find((r) => r.id === refId);
      reviewTo = r ? r.name : '';
      recipientForReview = r || null;
      // Full address, never truncated — this is the signing confirmation
      // screen (CLAUDE.md: never hide a swapped middle section).
      reviewToAddress = r ? r.address : '';
    }
  }
  const sendNetwork = CONFIG.NET[m.ccy] || 'Reporting currency · no network';
  // The chain a real broadcast actually settles on — same deductCcy
  // fallback as actions.js's buildTransferInputs (a reporting-currency
  // selection deducts from the source account's first real stablecoin
  // under the hood), not always m.ccy's own chain. Getting this wrong
  // would mislabel the fee's native-currency unit on the one screen
  // CLAUDE.md requires it be accurate on.
  const settlementCcy = m.ccy === s.reportingCurrency ? Object.keys(curAccount.balances)[0] : m.ccy;
  const settlementChain = CONFIG.NET[settlementCcy] || '';
  const row = (label, value) => h('div', { class: 'review-row' }, [
    h('span', { class: 'k' }, label),
    h('span', { class: 'v' }, value)
  ]);
  // Real on-chain fee quote (feature-backend.md item 11) — CLAUDE.md
  // requires fee to be visible before signing, so this always renders a
  // value: loading, an error, or the real quoted amount. Never a blank row.
  // Not fmtNum() -- its fixed 2-decimal-place formatting would round a
  // typical native-gas fee (e.g. 0.00021 ETH) down to "0.00", hiding the
  // fee CLAUDE.md requires be visible. reviewFee is already a clean
  // decimal string from the worker's own formatUnits call, human-readable
  // as-is.
  // feature-outstanding.md (2026-08-07): a gasless quote's fee is
  // denominated in the transferred token itself (reviewFeeAsset ===
  // 'token'), never the chain's native currency — labeling it with
  // NATIVE_CCY would be wrong, not just imprecise, for those.
  // m.reviewFeeError is now shown verbatim rather than a generic
  // "Unavailable" string: it's either a real, actionable message actions.js
  // already crafted (e.g. the insufficient-balance-for-fee check) or the
  // underlying quoteTransfer failure's own message — both more useful on a
  // signing-confirmation screen than hiding the real reason.
  const feeCcy = settlementCcy;
  // Currency-conversion "deliver as" (feature-swap.md) — mirrors
  // buildTransferInputs' own `swapping` condition (actions.js) exactly, so
  // the review screen can never show the swap rows for a move that will
  // actually sign as a plain transfer, or vice versa.
  const isSwapping = !!(m.deliverCcy && m.deliverCcy !== settlementCcy && swapDeliverAvailable(settlementChain));
  let feeValue;
  if (m.reviewFeeError) feeValue = m.reviewFeeError;
  else if (m.reviewFee != null) feeValue = m.reviewFee + ' ' + (m.reviewFeeAsset === 'token' ? feeCcy : (NATIVE_CCY[settlementChain] || ''));
  else feeValue = t('send.reviewFeeLoading');
  const feeRow = row(t('send.reviewFee'), feeValue);
  const gaslessNote = !m.reviewFeeError && m.reviewFee != null && m.reviewFeeAsset === 'token'
    ? h('div', { class: 'field-help' }, t('send.reviewFeeGasless', { native: NATIVE_CCY[settlementChain] || '', ccy: feeCcy }))
    : null;
  // Swap fee row replaces the plain feeRow entirely when isSwapping — the
  // plain reviewFee/reviewFeeError fields are never populated on the swap
  // path (fetchReviewFee's swapping branch), so feeRow above would show a
  // permanent "loading" state if left in place alongside these.
  const swapFeeVal = swapFeeDisplay(m, feeCcy, NATIVE_CCY[settlementChain] || '') || t('send.reviewFeeLoading');
  const swapFeeRow = row(m.reviewSwapNeedsApproval ? t('send.reviewApprovalFee') : t('send.reviewFee'), swapFeeVal);
  const swapFeeGaslessNote = !m.reviewSwapError &&
    (m.reviewSwapNeedsApproval ? m.reviewSwapApproveFeeAsset : m.reviewSwapFeeAsset) === 'token'
    ? h('div', { class: 'field-help' }, t('send.reviewFeeGasless', { native: NATIVE_CCY[settlementChain] || '', ccy: feeCcy }))
    : null;
  const swapOutVal = m.reviewSwapError
    ? '—'
    : (m.reviewSwapOut != null ? fmtNum(Number(m.reviewSwapOut)) + ' ' + m.deliverCcy : t('send.reviewFeeLoading'));
  const swapOutRow = row(t('send.reviewRecipientReceives'), swapOutVal);
  const swapMultiStepNote = h('div', { class: 'field-help' }, t('send.swapMultiStepNote'));
  // CLAUDE.md: "mainnet actions need a visually distinct, explicit
  // confirmation step" — real broadcast (feature-backend.md item 11) is
  // exactly that action. Testnet (the default) never shows this.
  const mainnetGuard = !s.testnetEnabled
    ? h('div', { class: 'mainnet-guard' }, [
        h('label', { class: 'checkbox-row mainnet-guard-row' }, [
          customCheckbox(m.mainnetAck, (e) => setMoveField('mainnetAck', e.target.checked)),
          t('send.mainnetAckLabel')
        ])
      ])
    : null;
  // Blocked whenever the real fee quote hasn't succeeded yet — loading,
  // errored (Tron/unreachable RPC/anything else quoteTransfer can throw),
  // or simply not fetched — never lets Confirm fire against an amount
  // whose real cost isn't actually known. Same reasoning covers Tron
  // without a client-side chain check: quoteTransfer already throws for
  // it, which lands here as reviewFeeError. Swap path: same shape, but
  // gated on whichever of approveFee/fee is the currently-relevant one
  // (mutually exclusive by design, swapFeeDisplay's own reasoning) *and*
  // on the swap quote (reviewSwapOut) having resolved — Confirm must never
  // fire while "recipient receives ~X" is still unknown.
  const swapConfirmBlocked = m.reviewSwapLoading || !!m.reviewSwapError || m.reviewSwapOut == null ||
    (m.reviewSwapNeedsApproval ? m.reviewSwapApproveFee == null : m.reviewSwapFee == null);
  const confirmBlocked = m.sending || (!s.testnetEnabled && !m.mainnetAck) ||
    (isSwapping ? swapConfirmBlocked : (m.reviewFeeLoading || !!m.reviewFeeError || m.reviewFee == null));
  // feature-harden.md 6.6: the enforced per-account limit stays visible on
  // the actual signing-confirmation screen, not just the form — including
  // whether this specific send only got here via a re-auth override.
  const limitOverridden = curAccount.sendLimit != null &&
    m.limitOverrideForAmount === m.amount && m.limitOverrideForCcy === m.ccy && m.limitOverrideForAccountId === m.fromAccountId;
  const limitRow = curAccount.sendLimit != null
    ? row(t('send.reviewSendLimit'), fmtReporting(curAccount.sendLimit) + (limitOverridden ? ' · ' + t('send.reviewLimitOverridden') : ''))
    : null;
  // CCSS-KU (AUDIT.md F3): same "stays visible on the review/signing-
  // confirmation screen, not just the form" requirement as the send limit
  // above -- shown whenever this is still an unverified recipient's first
  // payment, whether or not the cooldown/echo-test override was needed to
  // get here.
  const newRecipientOverridden = m.newRecipientOverrideForToKey === m.toKey;
  const newRecipientRow = recipientForReview && !recipientForReview.firstPaymentVerified
    ? row(t('send.reviewNewRecipient'), newRecipientOverridden ? t('send.reviewNewRecipientOverridden') : t('send.reviewNewRecipientNotice'))
    : null;

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, t('send.reviewTitle')),
    h('div', { class: 'card stack' }, [
      row(t('send.reviewFrom'), curAccount.name + ' · ' + m.ccy),
      row(t('send.reviewTo'), reviewTo),
      reviewToAddress ? h('div', { class: 'mono review-address' }, reviewToAddress) : null,
      // Reached only via review step, i.e. after validateMove has already
      // confirmed m.amount is a strict decimal string — Number() is exact.
      row(t('send.reviewAmount'), fmtNum(Number(m.amount || 0)) + ' ' + m.ccy),
      row(t('send.reviewNetwork'), sendNetwork),
      // feature-harden.md 6.8 / feature-outstanding.md: memo was captured
      // in state and length-capped but never actually shown here — the
      // one real gap that item left open. Only rendered when non-empty
      // (unlike the fee/network rows above, a memo is genuinely optional,
      // so an empty row would just be clutter on the signing-confirmation
      // screen). Text-child path only (h()'s textContent sink, never
      // innerHTML) — same as every other row here. Deliberately NOT sent
      // to the worker: WDK's EvmTransferOptions/TransferOptions (Tron)
      // have no memo/data field at all — ERC-20/TRC-20 transfers are
      // smart-contract calls with no on-chain memo slot, so there is
      // nothing for signTransfer to attach this to. Shown here as a
      // local reference/note only, same as the form already documents it.
      m.memo ? row(t('send.reviewMemo'), m.memo) : null,
      isSwapping ? swapFeeRow : feeRow,
      isSwapping ? swapFeeGaslessNote : gaslessNote,
      isSwapping ? swapOutRow : null,
      isSwapping ? swapMultiStepNote : null,
      row(t('send.reviewArrival'), t('send.reviewArrivalOnChain')),
      limitRow,
      newRecipientRow
    ]),
    mainnetGuard,
    m.errors.submit ? h('div', { class: 'field-error' }, m.errors.submit) : null,
    h('div', { class: 'btn-row' }, [
      h('button', { class: 'btn btn-secondary', disabled: m.sending, onclick: onMoveBack }, t('send.back')),
      h('button', {
        class: 'btn btn-primary btn-emphasis',
        disabled: confirmBlocked,
        onclick: confirmBlocked ? null : onMoveConfirm
      }, m.sending ? t('send.sending') : t('send.confirmTransfer'))
    ])
  ]);
}

// Desktop split-pane's right side while step === 'form' (style.css's
// .send-split) — deliberately not renderReview(s) with its Confirm button
// sitting there live: CLAUDE.md requires every signing action behind an
// explicit, human-readable confirmation step, and a Confirm button that's
// already enabled next to the form (before the user has even pressed
// "Continue to review") would undercut that, split-pane or not. The real
// review only appears here once step actually becomes 'review' — same
// state transition onMoveContinue already gated it behind pre-split-pane.
function renderReviewPlaceholder() {
  return h('div', { class: 'card empty-state' }, t('send.reviewPlaceholder'));
}

function renderSuccess(s) {
  const tx = s.move.resultTx;
  // Real explorer link (feature-backend.md item 11) for the transaction
  // that just broadcast — the highest-value place for it, right when the
  // user most wants to verify it landed. tx.chain is the resolved
  // settlement chain (see actions.js's onMoveConfirm), not always m.ccy's
  // own chain.
  // hashPending (GasFree/gasless-Tron async submission — see
  // tether.worker.js's signTransfer comment): tx.hash is a relay request
  // id, not a real txHash, so there's no working explorer link to build
  // yet. Show pending copy instead rather than a dead link or a hash that
  // won't resolve on-chain. Activity resolves the real hash independently.
  const explorerHref = tx && !tx.hashPending ? explorerTxUrl(tx.chain, tx.hash, s.testnetEnabled) : null;
  // Currency-conversion "deliver as" outcome (feature-swap.md Phase 4's
  // resultTx.deliverCcy/tokenOutAmount/approveTxId) — only present for a
  // completed swap, null otherwise (onMoveConfirm's plain-transfer branch).
  const swapOutcome = tx && tx.deliverCcy
    ? h('div', { class: 'success-sub' }, t('send.successSwapOutcome', { amount: fmtNum(Number(tx.tokenOutAmount || 0)) + ' ' + tx.deliverCcy }))
    : null;
  return h('div', { class: 'success-block' }, [
    h('div', { class: 'success-badge' }, h('div', { class: 'check' })),
    h('div', { class: 'success-title' }, t('send.successTitle')),
    h('div', { class: 'success-sub' }, t('send.successSub', { amount: tx ? fmtNum(tx.amount) + ' ' + tx.ccy : '' })),
    swapOutcome,
    h('div', { class: 'ref-card' }, [
      t('send.ref', { id: tx ? tx.id : '' }), h('br'),
      tx && tx.hashPending ? t('send.hashPending') : t('send.hash', { hash: tx ? tx.hash : '' })
    ]),
    explorerHref ? h('a', { href: explorerHref, target: '_blank', rel: 'noopener noreferrer' }, t('activity.viewOnChain')) : null,
    h('button', { class: 'btn btn-primary', onclick: onMoveDone }, t('send.done'))
  ]);
}

export function renderSend() {
  const s = getState();
  // No accounts yet (item 10 dropped the seeded demo wallets) — every
  // From-account lookup below assumes at least one exists, so this must
  // route to an empty state rather than fall through and crash.
  if (s.wallets.length === 0) {
    return h('div', { class: 'screen' }, [
      h('h1', { class: 'screen-title' }, t('send.title')),
      h('div', { class: 'empty-state' }, t('send.noAccountsEmpty')),
      h('button', { class: 'btn btn-primary', onclick: openOpenAccount }, t('send.openAccount'))
    ]);
  }
  const step = s.move.step;

  // 'success' is terminal — nothing left to split the form against, so it
  // gets the same generic content+activity-rail two-column treatment
  // Receive/Transfer use (feature-ui.md's Desktop section), not the
  // form/review split-pane below.
  if (step === 'success') {
    return h('div', { class: 'screen-layout' }, [
      h('div', { class: 'screen screen-main' }, [renderSuccess(s)]),
      renderActivityRail(false)
    ]);
  }

  // Both panes always render regardless of step — style.css's .send-split
  // shows only the one matching the current step below the desktop
  // breakpoint (a plain CSS swap standing in for the old full-replace,
  // same visual result on mobile) and both side by side at/above it (the
  // actual split-pane, feature-ui.md's "Convert Send's step flow into a
  // split-pane instead of full replace on desktop").
  return h('div', { class: 'send-split step-' + step }, [
    h('div', { class: 'screen send-pane-form' }, [renderForm(s, step === 'review')]),
    h('div', { class: 'screen send-pane-review' }, [step === 'review' ? renderReview(s) : renderReviewPlaceholder()])
  ]);
}
